import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import { Pill } from 'components/Pill'
import TableHeader from '../Table/Header'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  Box,
  Tooltip,
} from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'

import { ComposeContextWrapper } from 'components/ComposeOutreachMessageButton/ContextWrappers'
import MessageCreatedNotification from '../MessageCreatedNotification'
import ErrorMessage from '../ErrorMessage'
import RequestFeatureAccessModal from '../RequestFeatureAccessModal'
import Actions from './Actions'
import ContactCounselorModal from 'components/ContactCounselorModal'
import { rowHeight } from '../Table/constants'
import RequestPermissionModal from '../RequestPermissionModal'

const useStyles = makeStyles((theme) => ({
  noOverflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pill: {
    borderColor: 'transparent',
    borderRadius: '4px',
    marginRight: theme.spacing(1),
  },
  table: {
    overflow: 'hidden',
    '& th, & td': {
      verticalAlign: 'top',
    },
    '& th:first-child, & td:first-child': {
      maxWidth: '0', //  black magic that makes the ellipses work on tables...
      width: '44%',
    },
  },
  schoolLocation: {
    marginBottom: theme.spacing(1),
  },
  pillMargin: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}))

const List = ({ error, loading, schools }) => {
  const rowsPerPage = 3
  const styles = useStyles()
  const [page, setPage] = useState(0)

  const [selectedHighSchool, setSelectedHighSchool] = useState({})
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false)
  const handleOpenActionsMenu = (highSchool) => {
    setSelectedHighSchool(highSchool)
    setIsActionsMenuOpen(true)
  }
  const handleCloseActionsMenu = () => setIsActionsMenuOpen(false)

  const [isContactCounselorModalOpen, setIsContactCounselorModalOpen] = useState(false)
  const handleOpenContactCounselorModal = (school) => {
    setSelectedHighSchool(school)
    setIsContactCounselorModalOpen(true)
  }
  const handleCloseContactCounselorModal = () => setIsContactCounselorModalOpen(false)

  const [accessModal, setAccessModal] = useState({})
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false)
  const openAccessModal = ({ feature, role }) => {
    setAccessModal({ feature, role })
    setIsAccessModalOpen(true)
  }
  const closeAccessModal = () => setIsAccessModalOpen(false)

  useEffect(() => setPage(0), [schools])

  const handleChangePage = (_event, newPage) => {
    setPage(newPage)
  }

  const emptyRows = loading ? rowsPerPage - 1 : rowsPerPage - Math.min(rowsPerPage, schools.length - page * rowsPerPage)

  return (
    <div>
      <TableContainer>
        <Table
          className={styles.table}
          size={'medium'}
          aria-label={'interested student high schools'}
        >
          <TableHeader
            columns={[
              { id: 'highSchool', label: 'High School' },
              { id: 'interestedStudents', label: 'Students', align: 'right' },
              { id: 'actions', label: 'Actions', align: 'right' },
            ]}
          />
          <TableBody>
            {loading ? (
              <React.Fragment>
                {[...Array(rowsPerPage).keys()].map((row, index) => (
                  <TableRow
                    key={index}
                    style={{ height: rowHeight }}
                  >
                    <TableCell>
                      <Typography
                        className={styles.noOverflow}
                        variant="body2"
                        color="textPrimary"
                      >
                        <Skeleton />
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                      >
                        <Skeleton width={109} />
                      </Typography>
                      <Skeleton
                        height={28}
                        width={109}
                      />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        <Skeleton
                          variant="circle"
                          width={5}
                          height={5}
                        />
                        <Skeleton
                          variant="circle"
                          width={5}
                          height={5}
                        />
                        <Skeleton
                          variant="circle"
                          width={5}
                          height={5}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ) : error ? (
              <TableRow style={{ height: rowHeight * rowsPerPage }}>
                <TableCell
                  colSpan={3}
                  style={{ verticalAlign: 'middle' }}
                >
                  <ErrorMessage />
                </TableCell>
              </TableRow>
            ) : (
              <React.Fragment>
                {schools.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((school) => (
                  <TableRow
                    style={{ height: rowHeight }}
                    key={school.id}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                    >
                      <Tooltip title={school.name}>
                        <Typography
                          className={styles.noOverflow}
                          variant="body2"
                          color="textPrimary"
                        >
                          {school.name}
                        </Typography>
                      </Tooltip>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        className={styles.schoolLocation}
                      >
                        {school.city}, {school.state}
                      </Typography>
                      {school.is_title_1 && (
                        <Pill
                          variant="secondary"
                          caption="Title 1 Eligible"
                          className={styles.pillMargin}
                        />
                      )}
                      {school.is_visit_scheduled && (
                        <Pill
                          variant="info"
                          caption="Visit Scheduled"
                          className={styles.pillMargin}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">{school.count}</TableCell>
                    <TableCell align="right">
                      <Actions
                        open={isActionsMenuOpen && selectedHighSchool.id == school.id}
                        handleOpen={handleOpenActionsMenu}
                        handleClose={handleCloseActionsMenu}
                        openAccessModal={openAccessModal}
                        openContactCounselorModal={handleOpenContactCounselorModal}
                        school={school}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: rowHeight * emptyRows }}>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[rowsPerPage]}
        component="div"
        count={schools.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
      />
      {accessModal.feature && (
        <RequestFeatureAccessModal
          feature={accessModal.feature}
          show={isAccessModalOpen}
          onHide={closeAccessModal}
        />
      )}
      {accessModal.role && (
        <RequestPermissionModal
          role={accessModal.role}
          show={isAccessModalOpen}
          onHide={closeAccessModal}
        />
      )}
      {selectedHighSchool && (
        <ContactCounselorModal
          open={isContactCounselorModalOpen}
          handleClose={handleCloseContactCounselorModal}
          highSchoolId={selectedHighSchool.id}
          highSchoolName={selectedHighSchool.name}
          contactUserId={selectedHighSchool.contact_user_id}
        />
      )}
    </div>
  )
}

List.propTypes = {
  error: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  schools: PropTypes.arrayOf(
    PropTypes.shape({
      city: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      is_title_1: PropTypes.bool.isRequired,
      is_visit_scheduled: PropTypes.bool.isRequired,
    }),
  ).isRequired,
}

const ListWrapper = (props) => {
  const [showNotification, setShowNotification] = useState(false)

  return (
    <ComposeContextWrapper onSave={() => setShowNotification(true)}>
      <List {...props} />
      <MessageCreatedNotification
        onDismiss={() => setShowNotification(false)}
        show={showNotification}
        fullWidth={false}
      />
    </ComposeContextWrapper>
  )
}

export default ListWrapper
